import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, Save, Loader2, Paperclip, X, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MyBusinessPage = ({ toast }) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [verLectura, setVerLectura] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/my-business`);
      setText(r.data.text || '');
      const cargados = (r.data.files || []).map(f => ({
        ...f,
        status: (f.extracted && f.extracted.trim()) ? 'ok' : 'error',
      }));
      setFiles(cargados);
      setUpdatedAt(r.data.updated_at);
      if (cargados.length > 0) setSavedAt(Date.now());
    } catch (e) {}
    setLoading(false);
  };

  // Lee y guarda UN archivo, mostrando su estado en pantalla
  const procesarArchivo = (file, index) => {
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataB64 = ev.target.result.split(',')[1];
      try {
        const r = await axios.post(`${BACKEND_URL}/api/my-business/file`, {
          name: file.name,
          type: file.type,
          data: dataB64,
          description: '',
        });
        const res = r.data || {};
        setFiles(prev => prev.map((f, i) => i === index ? {
          name: res.name || file.name,
          type: res.type || file.type,
          size: file.size,
          description: res.description || '',
          file_id: res.file_id,
          extracted: res.extracted,
          status: res.ok ? 'ok' : 'error',
        } : f));
      } catch (e) {
        setFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'error' } : f));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e.target.files || []);
    setSavedAt(null);
    uploadedFiles.forEach(file => {
      setFiles(prev => {
        const index = prev.length;
        setTimeout(() => procesarArchivo(file, index), 0);
        return [...prev, { name: file.name, type: file.type, size: file.size, description: '', status: 'reading' }];
      });
    });
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const save = async () => {
    setSaving(true);
    try {
      const filesMeta = files.map(f => ({
        name: f.name, type: f.type, description: f.description || '',
        file_id: f.file_id, extracted: f.extracted,
      }));
      await axios.put(`${BACKEND_URL}/api/my-business`, { text, files: filesMeta });
      setSavedAt(Date.now());
      toast({ title: 'Guardado', description: 'Todos los agentes ya tienen esta información.' });
      setUpdatedAt(new Date().toISOString());
    } catch (e) {
      toast({ title: 'Error guardando', variant: 'destructive' });
    }
    setSaving(false);
  };

  const leyendo = files.some(f => f.status === 'reading');

  if (loading) {
    return <div className="flex justify-center p-12"><Loader2 className="animate-spin" /></div>;
  }

  const EstadoArchivo = ({ f }) => {
    if (f.status === 'reading') return <span className="flex items-center gap-1 text-xs text-amber-600"><Loader2 className="h-3 w-3 animate-spin" /> Leyendo...</span>;
    if (f.status === 'ok') {
      if (savedAt) return <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle2 className="h-3 w-3" /> Leído y guardado</span>;
      return <span className="flex items-center gap-1 text-xs text-blue-600"><CheckCircle2 className="h-3 w-3" /> Leído · pulsa Guardar para confirmar</span>;
    }
    return <span className="flex items-center gap-1 text-xs text-red-500"><AlertCircle className="h-3 w-3" /> No se pudo leer · vuelve a subirlo</span>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" /> Mi Negocio
        </CardTitle>
        <CardDescription>
          Describe tu negocio con todo el detalle que puedas. Sin límites, sin casillas.
          Todos los agentes leen esto antes de cada análisis.
          {updatedAt && ` · Guardado: ${new Date(updatedAt).toLocaleString('es-ES')}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm text-slate-500 mb-2">
            Cuéntalo todo: qué vendes, a quién, cómo funciona, qué está pasando, qué no funciona,
            tus redes, tu web, tus objetivos, tu competencia, tus dudas... Todo cuenta.
          </p>
          <textarea
            className="w-full min-h-[400px] border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 resize-y"
            placeholder="Describe tu negocio aquí..."
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <p className="text-sm font-medium text-slate-700">Archivos adjuntos</p>
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              <Paperclip className="h-4 w-4 mr-1" /> Añadir archivo
            </Button>
          </div>
          <input type="file" ref={fileInputRef} onChange={handleFileUpload}
            accept=".pdf,.png,.jpg,.jpeg,.webp" multiple className="hidden" />
          <p className="text-xs text-slate-400 mb-2">PDF del producto, capturas, logo, imágenes... Cada archivo se lee al subirlo.</p>

          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((file, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded border">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-slate-700 truncate block">
                      {file.name}
                      {file.size ? <span className="text-xs text-slate-400 ml-2">{(file.size / 1048576).toFixed(2)} MB</span> : null}
                    </span>
                    <EstadoArchivo f={file} />
                  </div>
                  {file.status === 'ok' && file.extracted ? (
                    <button onClick={() => setVerLectura(file)} className="text-xs text-blue-600 underline whitespace-nowrap">
                      Ver lo leído
                    </button>
                  ) : null}
                  <input
                    className="text-xs border rounded px-2 py-1 w-40"
                    placeholder="Describe este archivo (opcional)"
                    value={file.description || ''}
                    onChange={(e) => {
                      const updated = [...files];
                      updated[i].description = e.target.value;
                      setFiles(updated);
                    }}
                  />
                  <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button onClick={save} disabled={saving || leyendo || !text.trim()} className="w-full">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {leyendo ? 'Espera, leyendo archivos...' : 'Guardar'}
        </Button>
      </CardContent>

      {verLectura && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setVerLectura(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-medium text-slate-800 truncate">Lo que se ha leído de: {verLectura.name}</p>
              <button onClick={() => setVerLectura(null)} className="text-slate-400 hover:text-red-500"><X className="h-5 w-5" /></button>
            </div>
            <pre className="text-xs text-slate-700 whitespace-pre-wrap">{verLectura.extracted || 'Sin contenido.'}</pre>
          </div>
        </div>
      )}
    </Card>
  );
};

export default MyBusinessPage;
